import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { NavigationNotificationsResponse } from "~vue/api/generated/navigation";
import { MainMenuPane, type PaneNotificationCounts } from "~vue/components/navigation/menuPanes";
import { computed, onMounted, onUnmounted, type Ref, ref } from "vue";

type SidebarNotifications = {
    panes: MainMenuPane[];
    counts: PaneNotificationCounts;
};

export interface UseSidebarNotificationsReturn {
    panesWithNotifications: Ref<MainMenuPane[]>;
    paneNotificationCounts: Ref<PaneNotificationCounts>;
    isLoading: Ref<boolean>;
    refresh: () => Promise<void>;
    dismissNotificationLocally: (paneToDismiss: MainMenuPane) => void;
}

function withCountsDeprioritizedByMarbles(notifications: SidebarNotifications): SidebarNotifications {
    const hasMarble = notifications.panes.some((pane) => (notifications.counts[pane] ?? 0) <= 0);
    return hasMarble ? { ...notifications, counts: {} } : notifications;
}

/**
 * Composable to fetch sidebar notification indicators from the API.
 *
 * The API returns which navigation panes should show a notification bubble
 * (e.g., performance_management when user has incomplete tasks) and, for panes
 * that show a numeric badge, the count to display (e.g., my_work new insights).
 *
 * Marbles take precedence: while any notifying pane has no count, every notifying
 * pane drops its numeric badge and shows a marble, so the sidebar never mixes the
 * two indicators.
 */
export function useSidebarNotifications(): UseSidebarNotificationsReturn {
    const notifications = ref<SidebarNotifications>({
        panes: [],
        counts: {},
    });
    const displayedNotifications = computed(() => withCountsDeprioritizedByMarbles(notifications.value));
    const isLoading = ref(false);
    let refreshGeneration = 0;
    const locallyDismissedPanes = new Set<MainMenuPane>();

    const refresh = async () => {
        const generation = ++refreshGeneration;
        isLoading.value = true;

        try {
            const { data, error } = await useFetch("/v1/navigation/notifications").json<NavigationNotificationsResponse>();

            if (generation !== refreshGeneration) {
                return;
            }
            if (!error.value && data.value) {
                const notifiedPanes = data.value.panes_with_notifications as MainMenuPane[];
                notifications.value = {
                    panes: notifiedPanes.filter((notifiedPane) => !locallyDismissedPanes.has(notifiedPane)),
                    counts: Object.fromEntries(Object.entries(data.value.pane_notification_counts ?? {}).filter(([pane]) => !locallyDismissedPanes.has(pane as MainMenuPane))),
                };
            } else {
                // Gracefully degrade - don't show notifications on error
                notifications.value = { panes: [], counts: {} };
            }
        } catch {
            if (generation !== refreshGeneration) {
                return;
            }
            // "Failed to fetch" errors are expected when users navigate away or have network issues.
            // Degrade gracefully by showing no notifications.
            notifications.value = { panes: [], counts: {} };
        } finally {
            if (generation === refreshGeneration) {
                isLoading.value = false;
            }
        }
    };

    const dismissNotificationLocally = (paneToDismiss: MainMenuPane) => {
        if (paneToDismiss === MainMenuPane.CONNECTIONS) {
            return;
        }
        locallyDismissedPanes.add(paneToDismiss);
        const updatedCounts = { ...notifications.value.counts };
        delete updatedCounts[paneToDismiss];
        notifications.value = {
            panes: notifications.value.panes.filter((notifiedPane) => notifiedPane !== paneToDismiss),
            counts: updatedCounts,
        };
    };

    const removeNavigateListener = router.on("navigate", () => {
        void refresh();
    });
    onUnmounted(removeNavigateListener);

    onMounted(() => {
        void refresh();
    });

    return {
        panesWithNotifications: computed(() => displayedNotifications.value.panes),
        paneNotificationCounts: computed(() => displayedNotifications.value.counts),
        isLoading,
        refresh,
        dismissNotificationLocally,
    };
}
